const _excluded = ["user", "claimGroup"],
  _excluded2 = ["user", "claim"],
  _excluded3 = ["user", "claimGroup"],
  _excluded4 = ["user", "claimGroup"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import { useEffect, useMemo, useState } from 'react';

/**
 * Creates a proxy that says that if the abilities for a certain page are not defined - then by default everything is true (allowed) for that page.
 * So e.g. abilities.page_1.can_see_button - may be defined as false for the UK, but we may not have any such definition for this page in Germany.
 * With this proxy we can simply write 
 *    if (abilities.page_1.can_see_button) - which will give false for the UK and true for Germany.
 * 
 * In addition we have defined a level of nesting on 'componemts' - check the array passed to createNestedPagesPropProxy.
 * The first nesting level is pages, followed by null, then components, followed by null.
 * This means that abilities.page_1.can_see_button will default to true, and abilities.page_1.components.component_1.can_see_button
 * will also default to true.
 * 
 * However abilities.page_1.components.component_1.someting.between.can_see_button - will evaluate to
 *         proxy     proxy  proxy      proxy       true     undefined  Error
 * 
 * If we dont have this proxy - we will need to have a lot of if statements / ternary operators everywhere in the code.
 * Or we will have to defined every single ability for every single market which may not be feasible.
 * 
 * @returns {Proxy} the proxy to the provided user abilities object
 */
export function createPagesAbilitiesProxy(userClaimGroupAbilities) {
  const createNestedPagesPropProxy = (abilitiesPages, nestedPropsArray) => {
    const currentLevelNestedPropName = nestedPropsArray === null || nestedPropsArray === void 0 ? void 0 : nestedPropsArray.shift();
    return new Proxy(abilitiesPages || {}, {
      get(target, prop) {
        if (prop in target) {
          const value = target[prop];
          if (typeof value === 'object' && value !== null) {
            return createNestedPagesPropProxy(value, nestedPropsArray);
          }
          return value;
        }
        if (prop === currentLevelNestedPropName || currentLevelNestedPropName === null) {
          return createNestedPagesPropProxy({}, nestedPropsArray);
        }
        return true;
      }
    });
  };
  const abilitiesProxy = new Proxy(userClaimGroupAbilities || {}, {
    get(abilitiesObject, prop) {
      if (prop === 'pages') {
        const res = createNestedPagesPropProxy(abilitiesObject[prop], [null, 'components', null]);
        return res;
      }
      return abilitiesObject[prop];
    }
  });
  return abilitiesProxy;
}

// default empty abilities implementation - so I dont have to copy paste this in every market
function getUserClaimGroupAbilities(_ref) {
  let {
      user,
      claimGroup
    } = _ref,
    additionalData = _objectWithoutProperties(_ref, _excluded);
  return createPagesAbilitiesProxy({
    pages: {}
  });
}

// default empty abilities implementation - so I dont have to copy paste this in every market
function getUserCompanyAbilities(user, companyId) {
  return createPagesAbilitiesProxy({
    pages: {}
  });
}
function getUserClaimAbilities(_ref2) {
  let {
      user,
      claim
    } = _ref2,
    params = _objectWithoutProperties(_ref2, _excluded2);
  return createPagesAbilitiesProxy({
    pages: {}
  });
}

// default useGetUserClaimGroupAbilities implementation - so I dont have to copy paste this in every market
export function useGetUserClaimGroupAbilities(_ref3) {
  let {
      user,
      claimGroup
    } = _ref3,
    additionalData = _objectWithoutProperties(_ref3, _excluded3);
  // it always initially returns this state, so we need to build it asap
  return getUserClaimGroupAbilities(_objectSpread({
    user,
    claimGroup
  }, additionalData));
}
export function useGetUserClaimAbilities(_ref4) {
  let {
      user,
      claimGroup
    } = _ref4,
    additionalData = _objectWithoutProperties(_ref4, _excluded4);
  // it always initially returns this state, so we need to build it asap
  return getUserClaimAbilities(_objectSpread({
    user,
    claimGroup
  }, additionalData));
}
export function useGetUserCompanyAbilities(user, companyId) {
  // it always initially returns this state, so we need to build it asap
  const [userCompanyAbilities, setUserCompanyAbilities] = useState(getUserCompanyAbilities(user, companyId));
  useEffect(() => {
    const claimGroupAbilities = getUserCompanyAbilities(user, companyId);
    setUserCompanyAbilities(claimGroupAbilities);
  }, [user, companyId]);
  return userCompanyAbilities;
}